// @ts-nocheck

class CountryDrawer extends HTMLElement {
  constructor() {
    super();
    this.onHeaderClick = this.onHeaderClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onTabClick = this.onTabClick.bind(this);
    this.onLayoutChange = this.onLayoutChange.bind(this);
  }

  connectedCallback() {
    // Ensure overlay lives at document.body level so it can cover the whole viewport
    let overlay = document.getElementById('CountryDrawer-Overlay');
    if (!overlay) {
      overlay = this.querySelector('#CountryDrawer-Overlay');
      if (overlay) {
        document.body.appendChild(overlay);
      } 
    }
    this.overlay = overlay;
    this.inner = this.querySelector('.drawer__inner');
    this.currentVal = this.querySelector('.country-current-val');
    this.changeTrigger = this.querySelector('.change-country-trigger');
    this.backTrigger = this.querySelector('.drawer__heading-TitleBack');
    // Country list is lazily mounted from a <template> to reduce initial DOM size.
    this.tabs = [];
    this.panels = [];

    // Set up responsive behavior (accordion vs desktop tabs)
    this.mediaQuery = window.matchMedia('(max-width: 899px)');
    this.isAccordionLayout = this.mediaQuery.matches;
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.onLayoutChange);
    } else if (this.mediaQuery.addListener) {
      // Safari < 14 fallback
      this.mediaQuery.addListener(this.onLayoutChange);
    }

    this.setAttribute('data-view', 'current');

    // Ensure the drawer element itself lives directly under <body> so it can
    // layer correctly, but do this once on connect to avoid jank on open().
    if (this.parentElement && this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    this.updateCurrentLabel();

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    if (this.changeTrigger) {
      this.changeTrigger.addEventListener('click', () => this.showList());
    }

    if (this.backTrigger) {
      this.backTrigger.addEventListener('click', () => this.showCurrent());
    }

    if (this.tabs.length && this.panels.length) {
      // Tab clicks (primarily for desktop)
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
      });

      // On mobile/tablet, make the column titles behave like accordion headers
      if (this.isAccordionLayout) {
        this.panels.forEach((panel) => this.bindAccordionHeader(panel));

        // Initial state on accordion layout: all sections closed
        this.tabs.forEach((tab) => {
          tab.setAttribute('aria-selected', 'false');
          tab.classList.remove('is-active');
        });
        this.panels.forEach((panel) => {
          panel.removeAttribute('hidden'); // keep all in flow; CSS collapses content
          panel.setAttribute('aria-hidden', 'true');
        });
      } else {
        // Desktop: activate the first tab by default
        const initialTab = this.tabs[0].dataset.tab;
        this.activateTab(initialTab);
      }
    }

    document.addEventListener('click', this.onHeaderClick);
    document.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onHeaderClick);
    document.removeEventListener('keydown', this.onKeyDown);

    if (this.mediaQuery) {
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.onLayoutChange);
      } else if (this.mediaQuery.removeListener) {
        this.mediaQuery.removeListener(this.onLayoutChange);
      }
    }
  }

  updateCurrentLabel() {
    if (!this.currentVal) return;
    const label = this.dataset.currentCountry || '';
    this.currentVal.textContent = label;
  }

  onHeaderClick(event) {
    const trigger = event.target.closest('[data-open-country-drawer="true"]');
    if (!trigger) return;
    event.preventDefault();
    this.open();
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.dataset.open === 'true') {
      this.close();
    }
  }

  onTabClick(event) {
    const tab = event.currentTarget;
    const tabName = tab.dataset.tab;
    if (!tabName) return;
    this.activateTab(tabName);
  }

  onLayoutChange(event) {
    this.isAccordionLayout = event.matches;

    if (!this.tabs || !this.panels) return;

    if (this.isAccordionLayout) {
      // Switch to accordion: all sections closed by default
      // and make column titles behave like accordion headers
      this.panels.forEach((panel) => this.bindAccordionHeader(panel));

      this.tabs.forEach((tab) => {
        tab.setAttribute('aria-selected', 'false');
        tab.classList.remove('is-active');
      });
      this.panels.forEach((panel) => {
        panel.removeAttribute('hidden'); // keep all in flow; CSS handles collapse
        panel.setAttribute('aria-hidden', 'true');
      });
    } else {
      // Switch to desktop tabs: ensure one tab is active and others hidden
      const activeTab =
        this.tabs.find((tab) => tab.classList.contains('is-active')) || this.tabs[0];
      const tabName = activeTab && activeTab.dataset.tab;
      if (tabName) {
        this.activateTab(tabName);
      }
    }
  }

  activateTab(tabName) {
    if (!this.tabs || !this.panels) return;

    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.tab === tabName;
      tab.setAttribute('aria-selected', String(isActive));
      tab.classList.toggle('is-active', isActive);
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.tab === tabName;

      // On desktop, we hide inactive panels completely.
      // On tablet/mobile (accordion layout), we keep all panels in the flow
      // and only toggle aria-hidden; CSS will collapse/expand the content.
      if (this.isAccordionLayout) {
        panel.removeAttribute('hidden');
      } else {
        panel.toggleAttribute('hidden', !isActive);
      }

      panel.setAttribute('aria-hidden', String(!isActive));
    });
  }

  bindAccordionHeader(panel) {
    const header = panel.querySelector('.column-title');
    if (!header || header.dataset.accordionBound) return;

    header.addEventListener('click', () => {
      const tabName = panel.dataset.tab;
      if (!tabName) return;

      // In accordion mode, clicking an open section closes it (toggle behaviour)
      if (this.isAccordionLayout) {
        const isCurrentlyOpen = panel.getAttribute('aria-hidden') === 'false';
        const tab = this.tabs.find((t) => t.dataset.tab === tabName);

        if (isCurrentlyOpen) {
          panel.setAttribute('aria-hidden', 'true');
          if (tab) {
            tab.setAttribute('aria-selected', 'false');
            tab.classList.remove('is-active');
          }
          return;
        }
      }

      // Otherwise, open this panel (and close the others in accordion mode)
      this.activateTab(tabName);
    });

    header.dataset.accordionBound = 'true';
  }

  open() {
    this.setAttribute('data-open', 'true');
    document.body.classList.add('overflow-hidden');
    if (this.overlay) {
      this.overlay.classList.add('is-active');
    }
  }

  close() {
    this.setAttribute('data-open', 'false');
    document.body.classList.remove('overflow-hidden');
    if (this.overlay) {
      this.overlay.classList.remove('is-active');
    }
  }

  ensureListMounted() {
    if (this.dataset.listMounted === 'true') return;

    const mount = this.querySelector('#CountryDrawerListMount');
    const tpl = this.querySelector('#CountryDrawerListTemplate');
    if (!mount || !tpl) return;

    mount.innerHTML = '';

    const frag = tpl.content ? tpl.content.cloneNode(true) : null;
    if (frag) mount.appendChild(frag);

    this.tabs = Array.from(this.querySelectorAll('.Custom__TabList-Item[data-tab]'));
    this.panels = Array.from(this.querySelectorAll('.TabPanel[data-tab]'));

    // Re-evaluate layout and initialize the list UI
    this.mediaQuery = this.mediaQuery || window.matchMedia('(max-width: 899px)');
    this.isAccordionLayout = this.mediaQuery.matches;

    if (this.tabs.length && this.panels.length) {
      this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));

      if (this.isAccordionLayout) {
        this.panels.forEach((panel) => this.bindAccordionHeader(panel));
        this.tabs.forEach((tab) => {
          tab.setAttribute('aria-selected', 'false');
          tab.classList.remove('is-active');
        });
        this.panels.forEach((panel) => {
          panel.removeAttribute('hidden');
          panel.setAttribute('aria-hidden', 'true');
        });
      } else {
        const initialTab = this.tabs[0].dataset.tab;
        this.activateTab(initialTab);
      }
    }

    // Bind geoloc + click handlers only once the heavy list exists
    newGeoloc(this);

    this.dataset.listMounted = 'true';
  }

  showList() {
    this.ensureListMounted();
    this.setAttribute('data-view', 'list');
  }

  showCurrent() {
    this.setAttribute('data-view', 'current');
  }
}

if (!customElements.get('country-drawer')) {
  customElements.define('country-drawer', CountryDrawer);
}

/* New Geoloc */
function newGeoloc(drawerRoot) { 
  // Log immediately to verify that the script and handler are running
  const marketFromData = drawerRoot?.dataset.currentCountryCode;
  const market = window.current_market || marketFromData;

  console.log('[CountryDrawer] newGeoloc start', {
    current_market: window.current_market,
    marketFromData,
    finalMarket: market,
  });
  const selector = market
    ? `.select-country-link[data-country="${market}"]`
    : null;

  if (!selector) {
    console.warn('[CountryDrawer] newGeoloc: no market available (window.current_market and data-current-country-code are missing)');
    return;
  }

  const currEl = document.querySelector(selector),
    currTab = currEl.closest('.TabPanel');

  if (!currEl) {
    console.warn('[CountryDrawer] newGeoloc: no .select-country-link for selector', selector);
    return;
  }

  console.log('[CountryDrawer] newGeoloc init, using selector:', selector);

  currEl.classList.add('select-country-link--active');

  const isAccordionLayout = window.matchMedia('(max-width: 899px)').matches;
  const tabTrigger =
    currTab && currTab.id
      ? document.querySelector(
          `country-drawer .Custom__TabList-Item[aria-controls="${currTab.id}"]`
        )
      : null;

  if (currTab && tabTrigger) {
    currTab.dataset.active = 'true';

    // On mobile/tablet accordion, keep all sections closed on load.
    if (!isAccordionLayout) {
      currTab.setAttribute('aria-hidden', 'false');
      tabTrigger.setAttribute('aria-selected', 'true');
      tabTrigger.dataset.active = 'true';
      console.log('[CountryDrawer] activating tab for market:', window.current_market, 'tab id:', currTab.id);
    }
  }

  document.querySelectorAll('.select-country-link').forEach((el) => {
    // no-op, replaced by event delegation below
  });

  // Event delegation: avoid binding thousands of listeners
  if (!drawerRoot.dataset.countryClickBound) {
    drawerRoot.addEventListener('click', (event) => {
      const a = event.target.closest('.select-country-link');
      if (!a) return;
      newGetLinkAttr({ preventDefault: () => event.preventDefault(), target: a });
    });
    drawerRoot.dataset.countryClickBound = 'true';
  }

  document
    .querySelectorAll('.language-current-wrapper .language-val a')
    .forEach((el) => el.addEventListener('click', updateLanguageCookie));
}

async function newGetLinkAttr(event) {
  event.preventDefault();

  console.log('[CountryDrawer] country click', {
    href: event.target.getAttribute('href'),
    country: event.target.dataset.country,
  });

  let hrefValue = event.target.getAttribute('href');
  const localeArray = hrefValue.split('-');
  let locale = localeArray[0].replace('/', '');
  let country = event.target.dataset.country;

  country = sanitizeIsoCode(country);
  locale = country === 'US' ? 'en' : sanitizeLanguageCode(locale);

  if (country.length === 2 && locale.length === 2) {
    setCookieGeolocation('geolocator_preference', `country: ${country}, language: ${locale}`, 30);
  }

  const currentHostname = window.location.hostname;
  let pathname = window.location.pathname ? window.location.pathname : '';
  const search = window.location.search ? window.location.search : '';

  if (window.location.href.startsWith('https://us.paristexasbrand.com')) {
    if (pathname && pathname !== '') {
      const languages = ['it', 'es', 'de', 'fr'];
      for (const lang of languages) {
        if (pathname.startsWith(`/${lang}`)) {
          pathname = pathname.replace(`/${lang}`, '');
          if (pathname === '/') pathname = '';
          break;
        }
        if (pathname.startsWith('//')) {
          pathname = pathname.replace('//', '/');
        }
      }
    }
  }

  pathname = pathname.replace(/^\/[a-z]{2}-[a-z]{2,3}(\/|$)/, '/');

  if (hrefValue.endsWith('/')) {
    hrefValue = hrefValue.slice(0, -1);
  }

  if (!pathname.startsWith('/')) {
    pathname = `/${pathname}`;
  }

  if (hrefValue.includes('us.paristexasbrand.com')) {
    console.log('[CountryDrawer] clearing cart before redirect to US domain');
    await clearCart();
    const newUrl = normalizeLocaleUrl(
      `https://us.paristexasbrand.com${pathname}${search}`
    );
    console.log('[CountryDrawer] redirecting to US domain:', newUrl);
    window.location.href = newUrl;
  } else if (
    currentHostname === 'paris-texas-us.myshopify.com' ||
    currentHostname === 'us.paristexasbrand.com'
  ) {
    console.log('[CountryDrawer] clearing cart before redirect from US to main domain');
    await clearCart();
    const newUrl = normalizeLocaleUrl(`https://paristexasbrand.com${hrefValue}${pathname}${search}`);
    console.log('[CountryDrawer] redirecting from US to main domain:', newUrl);
    window.location.href = newUrl;
  } else {
    const footerForm = document.querySelector('#FooterCountryForm');
    const footerLink =
      footerForm &&
      footerForm.querySelector(`.link[data-value="${event.target.dataset.country}"]`);

    // If the footer form or link is missing (e.g. in the drawer/header), fall back to a direct redirect
    if (!footerForm || !footerLink) {
      const newUrl = normalizeLocaleUrl(`${hrefValue}${pathname}${search}`);
      console.log(
        '[CountryDrawer] no FooterCountryForm/link found, falling back to direct redirect:',
        newUrl
      );
      window.location.href = newUrl;
      return;
    }

    const localizationForm = document.querySelector('localization-form form');
    const localizationLocale = localizationForm.querySelector('[name="locale_code"]');
    localizationLocale.value = locale;

    const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    let returnToInput = localizationForm.querySelector('input[name="return_to"]');
    if (!(returnToInput instanceof HTMLInputElement)) {
      returnToInput = document.createElement('input');
      returnToInput.type = 'hidden';
      returnToInput.name = 'return_to';
      localizationForm.appendChild(returnToInput);
    }
    returnToInput.value = returnPath;

    // _cSc comes from the existing theme scripts (same as production)
    // eslint-disable-next-line no-undef
    _cSc(footerLink);
  }
}

function sanitizeIsoCode(code) {
  return code.replace(/[^A-Z]/g, '').slice(0, 2);
}

function sanitizeLanguageCode(code) {
  return code.replace(/[^a-z]/g, '').slice(0, 2);
}

function normalizeLocaleUrl(url) {
  try {
    const parsed = new URL(url);
    const normalizedPathname = parsed.pathname.replace(
      /^\/([a-z]{2})-([a-z]{2,3})-\2(\/|$)/,
      '/$1-$2$3'
    );
    parsed.pathname = normalizedPathname;
    return parsed.toString();
  } catch (e) {
    return url;
  }
}

function updateLanguageCookie(event) {
  const usDomain = window.location.hostname.includes('myshopify.com')
    ? 'https://paris-texas-us.myshopify.com'
    : 'https://us.paristexasbrand.com';

  let newLanguage;

  if (window.location.href.startsWith(usDomain)) {
    const localeArray = event.target.dataset.lang.split('/');
    const locale = localeArray[1] && localeArray[1] !== '' ? localeArray[1] : 'en';
    newLanguage = `language: ${locale}`;
  } else {
    const localeArray = event.target.dataset.lang.split('-');
    let locale = localeArray[0].replace('/', '');
    locale = sanitizeLanguageCode(locale);
    newLanguage = `language: ${locale}`;
  }

  let cookieValue = getCookieGeolocation('geolocator_preference');
  if (cookieValue) {
    console.log('[CountryDrawer] updating language in cookie', { newLanguage, previous: cookieValue });
    const updatedValue = cookieValue.replace(/language: [a-z]{2}/, newLanguage);
    setCookieGeolocation('geolocator_preference', updatedValue, 30);
  }
}

function getCookieGeolocation(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

function setCookieGeolocation(name, value, days) {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1); // 1 year
  const expires = `expires=${date.toUTCString()}`;
  const timestamp = new Date().toISOString();
  const domain = 'domain=.paristexasbrand.com';
  const valueWithTimestamp = `${value}, timestamp:${timestamp}`;

  if (window.location.hostname.includes('myshopify.com')) {
    document.cookie = `${name}=${valueWithTimestamp};${expires};path=/`;
  } else {
    document.cookie = `${name}=${valueWithTimestamp};${expires};${domain};path=/`;
  }
}

function clearCart() {
  return fetch('/cart/clear.js')
    .then((response) => response.json())
    .then((data) => {
      console.log('[CountryDrawer] cart cleared', data);
      return data;
    })
    .catch((error) => {
      console.error('[CountryDrawer] clearCart error', error);
      return error;
    });
}

/* New Geoloc */